import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "@/lib/api/useApi";
import { productQuery } from "@/lib/api/queries";
import { imgUrl } from "@/lib/format";
import { useFormatters } from "@/lib/format";
import { useLocalizedContent } from "@/lib/localize";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/store/Icon";
import { Pencil } from "lucide-react";

export type BiwSelection = {
  colorName_en: string;
  size: string;
  image?: string;
  hex?: string;
};

type Props = {
  product: any;
  selection: BiwSelection | null;
  onSelectionChange: (sel: BiwSelection | null) => void;
};

export function BuyItWithItem({ product: r, selection, onSelectionChange }: Props) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useFormatters();
  const { productName, colorName, sizeName } = useLocalizedContent();

  const [open, setOpen] = useState(false);
  const [colors, setColors] = useState<any[]>([]);
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [activeImg, setActiveImg] = useState(0);

  const checked = !!selection;

  // Query full product details including sizes, colors, images, stock, etc.
  const { data: fullProduct } = useApi(
    ["biw-detail", r.slug],
    () => productQuery(r.slug).queryFn(),
    { enabled: open || checked }
  );

  const productColors = fullProduct?.colors || r.colors || [];
  const productSizes = fullProduct?.sizes || r.sizes || [];

  useEffect(() => {
    if (productColors.length > 0 && !selectedColor) {
      setSelectedColor(productColors[0]);
    }
  }, [productColors]);

  useEffect(() => {
    if (productSizes.length > 0 && !selectedSize) {
      setSelectedSize(productSizes[0]);
    }
  }, [productSizes]);

  const activeColorObj = selectedColor || productColors[0];
  const colorImages = activeColorObj?.images?.length
    ? activeColorObj.images
    : activeColorObj?.product_color_images?.length
    ? activeColorObj.product_color_images
    : null;
  const mappedColorImages = colorImages?.map((img: any) =>
    typeof img === "string" ? img : img.url || img.image_url
  ) || [];
  const modalImages: string[] = mappedColorImages.length
    ? mappedColorImages
    : r.images?.length
    ? r.images
    : [""];

  const openModal = () => {
    setOpen(true);
    // Apply initial selection
    if (selection) {
      const foundColor = productColors.find((c: any) => c.name === selection.colorName_en);
      if (foundColor) setSelectedColor(foundColor);
      setSelectedSize(selection.size);
    } else {
      if (productColors.length > 0) setSelectedColor(productColors[0]);
      if (productSizes.length > 0) setSelectedSize(productSizes[0]);
    }
    setActiveImg(0);
  };

  const handleCheck = (val: boolean) => {
    if (val) {
      openModal();
    } else {
      onSelectionChange(null);
    }
  };

  const handleConfirm = () => {
    onSelectionChange({
      colorName_en: selectedColor?.name || productColors[0]?.name || "",
      size: selectedSize || productSizes[0] || "",
      hex: selectedColor?.hex || productColors[0]?.hex || "#000",
      image: modalImages[activeImg] || modalImages[0] || "",
    });
    setOpen(false);
  };

  const isStockAvailable = (color: string, size: string) => {
    if (!fullProduct?.stock) return true;
    return fullProduct.stock.some(
      (s: any) => s.color === color && s.size === size && s.qty > 0
    );
  };

  const currentItemImage = selection?.image || r.images?.[0] || productColors[0]?.images?.[0];

  return (
    <>
      <div
        className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${
          checked
            ? "border-primary bg-primary/5 shadow-sm"
            : "border-outline-variant/65 hover:border-primary/40 bg-surface"
        }`}
      >
        <Checkbox checked={checked} onCheckedChange={handleCheck} className="relative z-10" />
        <div onClick={openModal} className="flex flex-1 items-center gap-3 cursor-pointer min-w-0">
          <div className="w-14 h-14 rounded-md overflow-hidden bg-surface-container-low shrink-0 flex items-center justify-center">
            {imgUrl(currentItemImage) ? (
              <img
                src={imgUrl(currentItemImage)}
                alt={productName(r.name)}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name="image" className="text-[20px] text-on-surface-variant/40" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-sm font-semibold hover:text-primary transition-colors truncate">
              {productName(r.name)}
            </p>
            <p className="text-xs text-secondary font-bold">{formatPrice(r.price)}</p>
            {selection && (
              <p className="text-[10px] text-on-surface-variant/75 mt-0.5 flex flex-wrap gap-x-2">
                {selection.colorName_en && (
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full border border-outline-variant"
                      style={{ backgroundColor: selection.hex }}
                    />
                    {colorName(selection.colorName_en)}
                  </span>
                )}
                {selection.size && (
                  <span>
                    {t("product.size", { defaultValue: "Size" })}:{" "}
                    <b className="text-on-surface font-semibold">{sizeName(selection.size)}</b>
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
        {checked && (
          <button
            type="button"
            onClick={openModal}
            aria-label={t("common.edit")}
            className="p-2 rounded-md hover:bg-surface-container-low transition-colors text-on-surface-variant hover:text-primary cursor-pointer shrink-0"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg sm:max-w-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto bg-surface">
          {open && (
            <div className="p-5 sm:p-6 pt-12 sm:pt-6">
              <DialogTitle className="sr-only">{productName(r.name)}</DialogTitle>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="aspect-square rounded-lg overflow-hidden bg-surface-container-low relative mb-3">
                    {imgUrl(modalImages[activeImg]) ? (
                      <img
                        src={imgUrl(modalImages[activeImg])}
                        alt={productName(r.name)}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-on-surface-variant/40">
                        <Icon name="image" className="text-[48px]" />
                      </div>
                    )}
                  </div>
                  {modalImages.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {modalImages.map((img: string, i: number) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActiveImg(i)}
                          className="aspect-square rounded overflow-hidden border-2 transition-all cursor-pointer border-transparent"
                        >
                          <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col justify-between">
                  <div className="space-y-4">
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-deep-chocolate">
                      {productName(r.name)}
                    </h2>
                    <p className="text-lg sm:text-2xl text-secondary font-bold">
                      {formatPrice(r.price)}
                    </p>

                    {productColors.length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-wider mb-2 font-semibold">
                          {t("common.colour")}:{" "}
                          <span className="text-on-surface-variant font-normal">
                            {colorName(selectedColor?.name || "")}
                          </span>
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {productColors.map((c: any) => (
                            <button
                              key={c.name}
                              type="button"
                              onClick={() => {
                                setSelectedColor(c);
                                setActiveImg(0);
                              }}
                              style={{ backgroundColor: c.hex }}
                              className="w-9 h-9 rounded-full border-2 transition-all cursor-pointer border-transparent"
                              aria-label={colorName(c.name)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {productSizes.length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-wider mb-2 font-semibold">
                          {t("product.selectSize")}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {productSizes.map((s: string) => {
                            const oos = !isStockAvailable(selectedColor?.name || "", s);
                            const active = selectedSize === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => !oos && setSelectedSize(s)}
                                disabled={oos}
                                className={`min-w-11 h-10 px-3 rounded-md border text-sm font-semibold uppercase tracking-wider transition-all cursor-pointer ${
                                  oos
                                    ? "border-dashed border-outline-variant/50 text-on-surface-variant/40 line-through cursor-not-allowed bg-muted/20"
                                    : active
                                    ? "border-primary bg-primary text-white shadow"
                                    : "border-outline-variant hover:border-primary hover:text-primary"
                                }`}
                              >
                                {sizeName(s)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-6">
                    <Button
                      variant="outline"
                      type="button"
                      className="flex-1 cursor-pointer"
                      onClick={() => setOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="button"
                      className="flex-1 bg-secondary text-white hover:bg-secondary/90 cursor-pointer"
                      onClick={handleConfirm}
                    >
                      {t("common.confirm", { defaultValue: "Confirm" })}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
